import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";

// ════════════════════════════════════════════════════════════════════════
//  GoldField — the cream/gold input look of the admin Profile form
//  (Settings → Account → Personal Details): label ABOVE the field, cream
//  rounded box, optional icon on the left, gold border + soft glow on focus.
//
//  A thin wrapper around MUI <TextField> — every TextField prop (value,
//  onChange, select + <MenuItem> children, multiline, error, helperText,
//  disabled, placeholder, InputProps, SelectProps, …) is passed straight
//  through, so swapping `<TextField label="X" …>` for `<GoldField label="X" …>`
//  changes only how the field looks, never how it behaves.
//
//    <GoldField label="Bank Name" icon={AccountBalanceIcon}
//      value={form.bank_name} onChange={(e) => onChange("bank_name", e.target.value)} />
//
//  `icon` is a component (e.g. PersonIcon), not an element.
// ════════════════════════════════════════════════════════════════════════

export const GOLD_FIELD = {
  label: "#2a241b",
  text: "#1d1a14",
  placeholder: "#7d7466",
  icon: "#3d3528",
  bg: "#f7f2e8",
  bgFocus: "#fffaf0",
  border: "#eadfcb",
  borderHover: "#d8c49a",
  focus: "#b8892c",
  error: "#c2410c",
};

const goldAutofill = {
  WebkitBoxShadow: `0 0 0 1000px ${GOLD_FIELD.bg} inset !important`,
  WebkitTextFillColor: `${GOLD_FIELD.text} !important`,
  caretColor: GOLD_FIELD.text,
  transition: "background-color 9999s ease-in-out 0s, color 9999s ease-in-out 0s",
};

export const goldFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "8px !important",
    background: `${GOLD_FIELD.bg} !important`,
    color: GOLD_FIELD.text,
    fontSize: "0.86rem",
    transition: "background 0.2s, box-shadow 0.2s",
    // !important — the app's MUI theme paints its own (purple) focus border
    // on .MuiOutlinedInput-notchedOutline with higher priority otherwise.
    "& .MuiOutlinedInput-notchedOutline": { borderColor: `${GOLD_FIELD.border} !important`, borderWidth: "1px !important" },
    "&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline": { borderColor: `${GOLD_FIELD.borderHover} !important` },
    "&.Mui-focused": { background: `${GOLD_FIELD.bgFocus} !important`, boxShadow: "0 0 0 3px rgba(184,137,44,0.15) !important" },
    // index.css paints a spinning purple gradient ring (::before) on every
    // focused outlined input — the profile form has none, so switch it off here.
    "&.Mui-focused::before": { display: "none !important", animation: "none !important" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${GOLD_FIELD.focus} !important` },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: `${GOLD_FIELD.error} !important` },
    "&.Mui-disabled": { opacity: 0.75 },
  },
  "& .MuiOutlinedInput-input": { py: "11px", color: `${GOLD_FIELD.text} !important`, WebkitTextFillColor: `${GOLD_FIELD.text} !important` },
  "& .MuiOutlinedInput-input::placeholder": { color: GOLD_FIELD.placeholder, WebkitTextFillColor: GOLD_FIELD.placeholder, opacity: 1 },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: 18, color: GOLD_FIELD.icon },
  "& .MuiSelect-icon": { color: GOLD_FIELD.text },
  "& em": { fontStyle: "normal", color: GOLD_FIELD.placeholder },
  "& .MuiFormHelperText-root": { mx: 0, mt: 0.6, fontSize: "0.72rem", color: GOLD_FIELD.placeholder },
  "& .MuiFormHelperText-root.Mui-error": { color: GOLD_FIELD.error },
  "& input:-webkit-autofill": goldAutofill,
  "& input:-webkit-autofill:hover": goldAutofill,
  "& input:-webkit-autofill:focus": goldAutofill,
  "& input:-webkit-autofill:active": goldAutofill,
};

// Cream dropdown menu for `select` fields — matches the profile form's menus.
export const goldMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      background: "#fffcf7",
      border: `1px solid ${GOLD_FIELD.border}`,
      borderRadius: "10px",
      boxShadow: "0 8px 28px rgba(122,87,18,0.14)",
      "& .MuiMenuItem-root": { color: GOLD_FIELD.text, fontSize: "0.86rem" },
      "& .MuiMenuItem-root.Mui-selected": { background: "rgba(184,137,44,0.14) !important" },
      "& .MuiMenuItem-root:hover": { background: "rgba(184,137,44,0.08)" },
    },
  },
};

// Label above the field — same size/weight as the profile form's labels.
export const GoldFieldLabel = ({ children, required, error }) => (
  <Typography component="label" sx={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: error ? GOLD_FIELD.error : GOLD_FIELD.label, mb: 0.8 }}>
    {children}
    {required && <Box component="span" sx={{ color: GOLD_FIELD.error }}> *</Box>}
  </Typography>
);

// A multi-line field (a textarea) keeps its leading icon at the TOP, level with the first line of text — not in the middle.
const multilineIconSx = (multiline) => (multiline ? {
  "& .MuiOutlinedInput-root": { alignItems: "flex-start" },
  "& .MuiInputAdornment-root": { alignSelf: "flex-start", height: "auto", maxHeight: "none", mt: "13px" },
} : {});

export default function GoldField({
  label, required, error, icon: Icon, sx, InputProps, SelectProps, select, fullWidth = true, ...rest
}) {
  const startAdornment = Icon
    ? <InputAdornment position="start"><Icon /></InputAdornment>
    : InputProps?.startAdornment;

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
      {label && <GoldFieldLabel required={required} error={error}>{label}</GoldFieldLabel>}
      <TextField
        {...rest}
        select={select}
        required={required}
        error={error}
        fullWidth={fullWidth}
        InputProps={{ ...InputProps, startAdornment }}
        SelectProps={select ? { ...SelectProps, MenuProps: { ...goldMenuProps, ...(SelectProps?.MenuProps || {}) } } : SelectProps}
        sx={[goldFieldSx, multilineIconSx(rest.multiline), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      />
    </Box>
  );
}
